import { memo, useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  FolderPlus,
  Plus,
  Loader2,
  Pencil,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { erpKnowledgeBaseService } from '@/services/erpKnowledgeBaseService';
import { useUserRoles } from '@/hooks/useUserRoles';
import { logger } from '@/utils/logger';

/**
 * Folder Manager, relocated from its old standalone page
 * (/dashboard/admin/knowledge) into a side panel on the upload page.
 * Functionality is unchanged: browse the KB folder tree, create, rename
 * and delete sections. Folder assignment of documents still happens at
 * the "Add to Knowledge Base" step - this panel only manages the tree.
 */

interface FolderData {
  id: string;
  name: string;
  description?: string;
  children_count: number;
  parent_id?: string;
}

interface FolderItemProps {
  folder: FolderData;
  level: number;
  isExpanded: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  onToggle: () => void;
  onAddChild: () => void;
  onEdit: () => void;
  onDelete: () => void;
  renderChildren?: () => React.ReactNode;
}

const FolderItem: React.FC<FolderItemProps> = memo(({
  folder,
  level,
  isExpanded,
  isLoading,
  isAdmin,
  onToggle,
  onAddChild,
  onEdit,
  onDelete,
  renderChildren,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const paddingLeft = level * 16;
  const hasChildren = folder.children_count > 0;

  return (
    <div>
      <div
        className="group flex cursor-pointer items-center rounded-lg px-2 py-1.5 transition-colors hover:bg-muted/50"
        style={{ paddingLeft: `${paddingLeft + 8}px` }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onToggle}
      >
        {/* Expand/Collapse Arrow */}
        <div className="mr-1 flex h-5 w-5 items-center justify-center">
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : hasChildren ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )
          ) : (
            <span className="w-4" />
          )}
        </div>

        {/* Folder Icon */}
        {isExpanded && hasChildren ? (
          <FolderOpen className="mr-2 h-4 w-4 flex-shrink-0 text-amber-400" />
        ) : (
          <Folder className="mr-2 h-4 w-4 flex-shrink-0 text-amber-400" />
        )}

        {/* Folder Name */}
        <span className="min-w-0 flex-1 truncate text-sm">{folder.name}</span>

        {/* Sub-section count (design-style zero-padded, muted) */}
        {hasChildren && !isHovered && (
          <span className="ml-2 font-mono text-[10px] text-muted-foreground/70">
            {String(folder.children_count).padStart(2, '0')}
          </span>
        )}

        {/* Action buttons - visible only on hover */}
        {isAdmin && (
          <div
            className={`flex items-center transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-blue-400"
              onClick={onEdit}
              title="Rename"
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-red-400"
              onClick={onDelete}
              title="Delete"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-primary"
              onClick={onAddChild}
              title="Add sub-section"
            >
              <Plus className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>

      {/* Children */}
      {isExpanded && renderChildren && <div>{renderChildren()}</div>}
    </div>
  );
});

FolderItem.displayName = 'FolderItem';

// Dialog for creating folders
interface FolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentName?: string;
  isLoading?: boolean;
  onSubmit: (name: string) => Promise<void>;
}

const FolderDialog: React.FC<FolderDialogProps> = ({
  open,
  onOpenChange,
  parentName,
  isLoading,
  onSubmit,
}) => {
  const [name, setName] = useState('');

  useEffect(() => {
    if (open) {
      setName('');
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Section name is required');
      return;
    }
    await onSubmit(name.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark bg-background text-foreground">
        <DialogHeader>
          <DialogTitle>Create Section</DialogTitle>
          {!parentName && (
            <DialogDescription>
              Add a new section to the knowledge base
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {parentName && (
              <div className="text-sm text-muted-foreground">
                Creating inside: <span className="font-medium text-foreground">{parentName}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="fm-name">Section name</Label>
              <Input
                id="fm-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter section name"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Dialog for editing folders
interface EditFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folder?: FolderData;
  isLoading?: boolean;
  onSubmit: (name: string) => Promise<void>;
}

const EditFolderDialog: React.FC<EditFolderDialogProps> = ({
  open,
  onOpenChange,
  folder,
  isLoading,
  onSubmit,
}) => {
  const [name, setName] = useState('');

  useEffect(() => {
    if (open && folder) {
      setName(folder.name);
    }
  }, [open, folder]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Section name is required');
      return;
    }
    await onSubmit(name.trim());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark bg-background text-foreground">
        <DialogHeader>
          <DialogTitle>Edit Section</DialogTitle>
          <DialogDescription>
            Update the section name
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="fm-edit-name">Section name</Label>
              <Input
                id="fm-edit-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter section name"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Dialog for delete confirmation
interface DeleteFolderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folder?: FolderData;
  isLoading?: boolean;
  onConfirm: () => Promise<void>;
}

const DeleteFolderDialog: React.FC<DeleteFolderDialogProps> = ({
  open,
  onOpenChange,
  folder,
  isLoading,
  onConfirm,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark bg-background text-foreground">
        <DialogHeader>
          <DialogTitle>Delete Section</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete "{folder?.name}"? This action cannot be undone.
            {folder && folder.children_count > 0 && (
              <span className="mt-2 block font-medium text-red-400">
                Warning: This section contains {folder.children_count} sub-section(s).
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={isLoading}
            onClick={onConfirm}
          >
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface FolderManagerPanelProps {
  className?: string;
}

const FolderManagerPanel: React.FC<FolderManagerPanelProps> = memo(({ className }) => {
  const { roles: userRoles, isSuperAdmin } = useUserRoles();
  const isAdmin = isSuperAdmin || userRoles.some(role => role.name === 'Admin');

  // Folder data stored by parent ID (empty string for root level)
  const [foldersByParent, setFoldersByParent] = useState<Map<string, FolderData[]>>(new Map());

  // Pagination state per parent folder
  const [paginationByParent, setPaginationByParent] = useState<Map<string, { page: number; hasNext: boolean; totalCount: number }>>(new Map());
  const [loadingMore, setLoadingMore] = useState<Set<string>>(new Set());

  // Loading and expanded state
  const [loadingFolders, setLoadingFolders] = useState<Set<string>>(new Set());
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Dialog state
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    parentId?: string;
    parentName?: string;
  }>({ open: false });
  const [isCreating, setIsCreating] = useState(false);

  // Edit dialog state
  const [editDialogState, setEditDialogState] = useState<{
    open: boolean;
    folder?: FolderData;
  }>({ open: false });
  const [isEditing, setIsEditing] = useState(false);

  // Delete dialog state
  const [deleteDialogState, setDeleteDialogState] = useState<{
    open: boolean;
    folder?: FolderData;
  }>({ open: false });
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch folders for a given parent (empty string = root level)
  const fetchFolders = useCallback(async (parentId: string = '', page = 1) => {
    try {
      if (parentId) {
        setLoadingFolders(prev => new Set(prev).add(parentId));
      }
      if (page > 1) {
        setLoadingMore(prev => new Set(prev).add(parentId));
      }

      const result = await erpKnowledgeBaseService.getFolderContents(parentId || undefined, true, page);
      // Filter to only show folders (is_folder === 1), hide files
      const foldersData = (result.data || [])
        .filter((item: any) => item.is_folder === 1)
        .map((item: any) => ({
          id: item.name,
          name: item.document_name || item.name,
          description: item.description,
          children_count: item.folders_count || 0,
          parent_id: parentId,
        }));

      setFoldersByParent(prev => {
        const next = new Map(prev);
        if (page > 1) {
          // Append to existing folders
          const existing = next.get(parentId) || [];
          next.set(parentId, [...existing, ...foldersData]);
        } else {
          next.set(parentId, foldersData);
        }
        return next;
      });

      // Track pagination state
      if (result.pagination) {
        setPaginationByParent(prev => {
          const next = new Map(prev);
          next.set(parentId, {
            page: result.pagination!.page,
            hasNext: result.pagination!.has_next,
            totalCount: result.pagination!.total_count,
          });
          return next;
        });
      }

      return foldersData;
    } catch (error) {
      logger.error('Error fetching folders:', error);
      toast.error('Failed to load folders');
      return [];
    } finally {
      if (parentId) {
        setLoadingFolders(prev => {
          const next = new Set(prev);
          next.delete(parentId);
          return next;
        });
      }
      setLoadingMore(prev => {
        const next = new Set(prev);
        next.delete(parentId);
        return next;
      });
      setIsInitialLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchFolders('');
  }, [fetchFolders]);

  // "Sync Tree": drop caches + collapsed reload from the root
  const [isSyncing, setIsSyncing] = useState(false);
  const handleSyncTree = useCallback(async () => {
    setIsSyncing(true);
    try {
      erpKnowledgeBaseService.clearCache();
      setFoldersByParent(new Map());
      setPaginationByParent(new Map());
      setExpandedFolders(new Set());
      await fetchFolders('');
    } finally {
      setIsSyncing(false);
    }
  }, [fetchFolders]);

  // Toggle folder expansion (accordion - close siblings but keep parents open)
  const toggleFolder = useCallback(async (folderId: string) => {
    const isExpanded = expandedFolders.has(folderId);

    if (isExpanded) {
      // Close this folder and all its descendants
      setExpandedFolders(prev => {
        const next = new Set(prev);
        next.delete(folderId);
        return next;
      });
    } else {
      // Fetch children if not already loaded
      if (!foldersByParent.has(folderId)) {
        await fetchFolders(folderId);
      }

      // Find the parent of this folder to get siblings
      const folder = Array.from(foldersByParent.values())
        .flat()
        .find(f => f.id === folderId);
      const parentId = folder?.parent_id ?? '';

      // Get sibling folder IDs (folders with same parent)
      const siblings = (foldersByParent.get(parentId) || []).map(f => f.id);

      // Close siblings but keep parents open, then open this one
      setExpandedFolders(prev => {
        const next = new Set(prev);
        // Remove siblings from expanded set
        siblings.forEach(siblingId => {
          if (siblingId !== folderId) {
            next.delete(siblingId);
          }
        });
        // Add the clicked folder
        next.add(folderId);
        return next;
      });
    }
  }, [expandedFolders, foldersByParent, fetchFolders]);

  // Open create dialog
  const openCreateDialog = (parentId?: string, parentName?: string) => {
    setDialogState({ open: true, parentId, parentName });
  };

  const closeDialog = () => {
    setDialogState({ open: false });
  };

  // Create folder handler
  const handleCreateFolder = async (name: string) => {
    try {
      setIsCreating(true);

      await erpKnowledgeBaseService.createItem({
        document_name: name,
        is_folder: true,
        parent_directory: dialogState.parentId,
      });

      toast.success('Section created');
      erpKnowledgeBaseService.clearCache();

      // Refresh the parent's children list
      const parentId = dialogState.parentId || '';
      await fetchFolders(parentId);

      // If there's a parent, expand it and refresh its parent to update count
      if (dialogState.parentId) {
        setExpandedFolders(prev => new Set(prev).add(dialogState.parentId!));

        // Find the parent folder to get its parent_id
        const parentFolder = Array.from(foldersByParent.values())
          .flat()
          .find(f => f.id === dialogState.parentId);

        if (parentFolder?.parent_id !== undefined) {
          await fetchFolders(parentFolder.parent_id);
        } else {
          // Parent is at root level, refresh root
          await fetchFolders('');
        }
      }

      closeDialog();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create section');
    } finally {
      setIsCreating(false);
    }
  };

  // Open edit dialog
  const openEditDialog = (folder: FolderData) => {
    setEditDialogState({ open: true, folder });
  };

  const closeEditDialog = () => {
    setEditDialogState({ open: false });
  };

  // Edit folder handler
  const handleEditFolder = async (name: string) => {
    if (!editDialogState.folder) return;

    try {
      setIsEditing(true);

      await erpKnowledgeBaseService.updateItem({
        name: editDialogState.folder.id,
        document_name: name,
      });

      toast.success('Section updated');
      erpKnowledgeBaseService.clearCache();

      // Refresh the parent's folder list
      const parentId = editDialogState.folder.parent_id || '';
      await fetchFolders(parentId);

      closeEditDialog();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update section');
    } finally {
      setIsEditing(false);
    }
  };

  // Open delete dialog
  const openDeleteDialog = (folder: FolderData) => {
    setDeleteDialogState({ open: true, folder });
  };

  const closeDeleteDialog = () => {
    setDeleteDialogState({ open: false });
  };

  // Delete folder handler
  const handleDeleteFolder = async () => {
    if (!deleteDialogState.folder) return;

    try {
      setIsDeleting(true);

      await erpKnowledgeBaseService.deleteItem(deleteDialogState.folder.id);

      toast.success('Section deleted');
      erpKnowledgeBaseService.clearCache();

      // Remove from expanded set if it was expanded
      setExpandedFolders(prev => {
        const next = new Set(prev);
        next.delete(deleteDialogState.folder!.id);
        return next;
      });

      // Refresh the parent's folder list
      const parentId = deleteDialogState.folder.parent_id || '';
      await fetchFolders(parentId);

      closeDeleteDialog();
    } catch (error: any) {
      // Extract error message - service throws Error with API message
      const errorMessage = error?.message || 'Failed to delete section';
      toast.error('Unable to delete', {
        description: errorMessage,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Load more folders for a given parent
  const loadMoreFolders = useCallback((parentId: string) => {
    const pagination = paginationByParent.get(parentId);
    if (pagination && pagination.hasNext) {
      fetchFolders(parentId, pagination.page + 1);
    }
  }, [paginationByParent, fetchFolders]);

  // Recursive folder renderer
  const renderFolders = (parentId: string, level: number): React.ReactNode => {
    const folders = foldersByParent.get(parentId) || [];
    const pagination = paginationByParent.get(parentId);
    const isLoadingMoreHere = loadingMore.has(parentId);

    return (
      <>
        {folders.map(folder => (
          <FolderItem
            key={folder.id}
            folder={folder}
            level={level}
            isExpanded={expandedFolders.has(folder.id)}
            isLoading={loadingFolders.has(folder.id)}
            isAdmin={isAdmin}
            onToggle={() => toggleFolder(folder.id)}
            onAddChild={() => openCreateDialog(folder.id, folder.name)}
            onEdit={() => openEditDialog(folder)}
            onDelete={() => openDeleteDialog(folder)}
            renderChildren={() => renderFolders(folder.id, level + 1)}
          />
        ))}
        {pagination?.hasNext && (
          <div style={{ paddingLeft: `${level * 16 + 8}px` }} className="px-2 py-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => loadMoreFolders(parentId)}
              disabled={isLoadingMoreHere}
              className="text-xs text-primary hover:bg-primary/10 hover:text-primary"
            >
              {isLoadingMoreHere ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Plus className="h-3 w-3 mr-1.5" />
                  Load more
                </>
              )}
            </Button>
          </div>
        )}
      </>
    );
  };

  const rootFolders = foldersByParent.get('') || [];

  return (
    <Card className={`overflow-hidden border-border bg-card ${className || ''}`}>
      <CardContent className="flex h-full flex-col p-0">
        {/* Panel header */}
        <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Intelligence Directory
            </p>
            <p className="text-sm font-semibold">Folder Manager</p>
          </div>
          {isAdmin && (
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => openCreateDialog()}
              title="New section"
            >
              <FolderPlus className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Tree */}
        <div className="max-h-[520px] flex-1 overflow-y-auto px-2 py-2">
          {isInitialLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading folders...</span>
            </div>
          ) : rootFolders.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              <Folder className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
              <p className="text-sm">No sections found</p>
            </div>
          ) : (
            <div>{renderFolders('', 0)}</div>
          )}
        </div>

        {/* Footer: Sync Tree */}
        <div className="border-t border-border p-3">
          <Button
            variant="outline"
            className="w-full"
            onClick={handleSyncTree}
            disabled={isSyncing || isInitialLoading}
          >
            {isSyncing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Sync Tree
          </Button>
        </div>
      </CardContent>

      {/* Create Folder Dialog */}
      <FolderDialog
        open={dialogState.open}
        onOpenChange={(open) => !open && closeDialog()}
        parentName={dialogState.parentName}
        isLoading={isCreating}
        onSubmit={handleCreateFolder}
      />

      {/* Edit Folder Dialog */}
      <EditFolderDialog
        open={editDialogState.open}
        onOpenChange={(open) => !open && closeEditDialog()}
        folder={editDialogState.folder}
        isLoading={isEditing}
        onSubmit={handleEditFolder}
      />

      {/* Delete Folder Dialog */}
      <DeleteFolderDialog
        open={deleteDialogState.open}
        onOpenChange={(open) => !open && closeDeleteDialog()}
        folder={deleteDialogState.folder}
        isLoading={isDeleting}
        onConfirm={handleDeleteFolder}
      />
    </Card>
  );
});

FolderManagerPanel.displayName = 'FolderManagerPanel';

export default FolderManagerPanel;
